import { t } from "@lingui/macro"
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Tooltip,
} from "@mui/material"
import {
    ExpandMore,
    Code,
    Message,
    SmartToy,
    AccountTree,
} from "@mui/icons-material"

import { IChatMessage } from "src/types/chat-message/chatMessage"
import { chatbot_NodeMetadata } from "src/api/models/chatbot_NodeMetadata"
import { chatbot_MessageInfoSource } from "src/api/models/chatbot_MessageInfoSource"

interface IPromptsSectionProps {
    message: IChatMessage
    _handleSourceClick?: (item?: chatbot_MessageInfoSource) => void
}

const PromptsSection = ({
    message,
    _handleSourceClick,
}: IPromptsSectionProps) => {
    const getNodeDisplayName = (node: chatbot_NodeMetadata) => {
        if (node.node_name != null && node.node_name !== "") {
            const nodeNameMap: Record<string, string> = {
                GetDataNode: "Get Data Node",
                ExpandInputNode: "Expand Input Node",
                NoAnswerNode: "No Answer Node",
                MapReplyNode: "Map Reply Node",
                EnoughInfoNode: "Enough Info Node",
                StoreResponseNode: "Store Response Node",
                SupervisorNode: "Supervisor Node",
                HasDataDecisionNode: "Has Data Decision Node",
                EnoughInfoDecisionNode: "Enough Info Decision Node",
                DecideErrandClassifiedNode: "Decide Errand Classified Node",
                AddConversationHistoryNode: "Add Conversation History Node",
                StoreUserInputNode: "Store User Input Node",
                SelectFormTypeNode: "Select Form Type Node",
                SubmitMessageNode: "Submit Message Node",
                EditNode: "Edit Node",
                AddActionsNode: "Add Actions Node",
                StoreLastActionNode: "Store Last Action Node",
                GetNextFieldNode: "Get Next Field Node",
                AttemptFieldAnswerNode: "Attempt Field Answer Node",
                SendNextQuestionNode: "Send Next Question Node",
                ConfirmSubmitNode: "Confirm Submit Node",
                SubmitFormNode: "Submit Form Node",
                ThanksForFillingOutFormNode: "Thanks For Filling Out Form Node",
                NotAnswerNode: "Not Answer Node",
                DispatchDecisionNode: "Dispatch Decision Node",
                ClarifyQuestionWithDataNode: "Clarify Question With Data Node",
                ClarifyQuestionForDispatchNode:
                    "Clarify Question For Dispatch Node",
                ConfirmContactDetailsNode: "Confirm Contact Details Node",
                ErrandNeedsMoreInfoNode: "Errand Needs More Info Node",
                MapClassificationReplyNode: "Map Classification Reply Node",
            }

            const nodeName = node.node_name
            if (nodeName != null && nodeNameMap[nodeName] !== undefined) {
                return nodeNameMap[nodeName]
            }

            if (nodeName != null) {
                let displayName = nodeName
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())
                    .trim()
                    .replace(/\s+/g, " ")

                return displayName
            }
        }
        return ""
    }

    const formatPrompt = (prompt: string) => {
        return prompt.split("\n").map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))
    }

    const formatResponse = (response: string) => {
        const trimmedResponse = response.trim()
        if (/^\d+$/.test(trimmedResponse)) {
            const num = parseInt(trimmedResponse, 10)
            const incrementedNum = num + 1
            return (
                <span>
                    {incrementedNum}
                    <br />
                </span>
            )
        }

        return response.split("\n").map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))
    }

    const getNodeTooltip = (nodeName: string) => {
        const tooltipMap: Record<string, string> = {
            GetDataNode: t`chat-message-journey.node-tooltips.get-data-node`,
            ExpandInputNode: t`chat-message-journey.node-tooltips.expand-input-node`,
            NoAnswerNode: t`chat-message-journey.node-tooltips.no-answer-node`,
            MapReplyNode: t`chat-message-journey.node-tooltips.map-reply-node`,
            EnoughInfoNode: t`chat-message-journey.node-tooltips.enough-info-node`,
            StoreResponseNode: t`chat-message-journey.node-tooltips.store-response-node`,
            SupervisorNode: t`chat-message-journey.node-tooltips.supervisor-node`,
            HasDataDecisionNode: t`chat-message-journey.node-tooltips.has-data-decision-node`,
            EnoughInfoDecisionNode: t`chat-message-journey.node-tooltips.enough-info-decision-node`,
            DecideErrandClassifiedNode: t`chat-message-journey.node-tooltips.decide-errand-classified-node`,
            AddConversationHistoryNode: t`chat-message-journey.node-tooltips.add-conversation-history-node`,
            StoreUserInputNode: t`chat-message-journey.node-tooltips.store-user-input-node`,
            SelectFormTypeNode: t`chat-message-journey.node-tooltips.select-form-type-node`,
            SubmitMessageNode: t`chat-message-journey.node-tooltips.submit-message-node`,
            EditNode: t`chat-message-journey.node-tooltips.edit-node`,
            AddActionsNode: t`chat-message-journey.node-tooltips.add-actions-node`,
            StoreLastActionNode: t`chat-message-journey.node-tooltips.store-last-action-node`,
            GetNextFieldNode: t`chat-message-journey.node-tooltips.get-next-field-node`,
            AttemptFieldAnswerNode: t`chat-message-journey.node-tooltips.attempt-field-answer-node`,
            SendNextQuestionNode: t`chat-message-journey.node-tooltips.send-next-question-node`,
            ConfirmSubmitNode: t`chat-message-journey.node-tooltips.confirm-submit-node`,
            SubmitFormNode: t`chat-message-journey.node-tooltips.submit-form-node`,
            ThanksForFillingOutFormNode: t`chat-message-journey.node-tooltips.thanks-for-filling-out-form-node`,
            NotAnswerNode: t`chat-message-journey.node-tooltips.not-answer-node`,
            DispatchDecisionNode: t`chat-message-journey.node-tooltips.dispatch-decision-node`,
            ClarifyQuestionWithDataNode: t`chat-message-journey.node-tooltips.clarify-question-with-data-node`,
            ClarifyQuestionForDispatchNode: t`chat-message-journey.node-tooltips.clarify-question-for-dispatch-node`,
            ConfirmContactDetailsNode: t`chat-message-journey.node-tooltips.confirm-contact-details-node`,
            ErrandNeedsMoreInfoNode: t`chat-message-journey.node-tooltips.errand-needs-more-info-node`,
            MapClassificationReplyNode: t`chat-message-journey.node-tooltips.map-classification-reply-node`,
        }

        return (
            tooltipMap[nodeName] ??
            t`chat-message-journey.node-tooltips.default`
        )
    }

    const getNodeType = (nodeName: string) => {
        const lowerName = nodeName.toLowerCase()

        if (lowerName.includes("decision") || lowerName.includes("decide")) {
            return "Decision"
        } else {
            return "State"
        }
    }

    const getNodeTypeTooltip = (nodeType: string) => {
        switch (nodeType) {
            case "Decision":
                return t`chat-message-journey.node-type-tooltips.decision`
            case "State":
                return t`chat-message-journey.node-type-tooltips.state`
            default:
                return t`chat-message-journey.node-type-tooltips.default`
        }
    }

    const getNodeColor = (nodeType: string) => {
        switch (nodeType) {
            case "Decision":
                return "secondary"
            case "State":
                return "primary"
            default:
                return "default"
        }
    }

    const getAgentInfo = () => {
        const allNodesData = message?.nodeInfo ?? []
        const uniqueAgents = new Set<string>()

        for (const node of allNodesData) {
            if (node.agent_name != null && node.agent_name !== "") {
                uniqueAgents.add(String(node.agent_name))
            }
        }
        return Array.from(uniqueAgents)
    }

    const getAgentDisplayName = (agentName: string) => {
        const agentNameMap: Record<string, string> = {
            dynamic_form_agent: "Dynamic forms",
            qa_agent: "QA",
            qualification_agent: "Qualification",
            dynamic_form: "Dynamic forms",
            public_agent: "Public",
            supervisor: "Supervisor",
        }
        return agentNameMap[agentName] ?? agentName
    }

    const getAgentColor = (agentName: string) => {
        const agentColorMap: Record<string, string> = {
            dynamic_form_agent: "rgba(156, 39, 176, 1)", // Purple
            qa_agent: "rgba(76, 175, 80, 1)", // Green
            qualification_agent: "rgba(255, 152, 0, 1)", // Orange
            dynamic_form: "rgba(233, 30, 99, 1)", // Pink
            public_agent: "rgba(0, 150, 136, 1)", // Teal
            supervisor: "rgba(121, 85, 72, 1)", // Brown
        }
        return agentColorMap[agentName] ?? "rgba(158, 158, 158, 1)" // Default gray
    }

    const getAgentIcon = (agentName: string) => {
        if (agentName === "supervisor") {
            return <AccountTree sx={{ fontSize: "18px" }} />
        }
        return <SmartToy sx={{ fontSize: "18px" }} />
    }

    const getNodesByAgent = () => {
        const allNodesData = message?.nodeInfo ?? []
        const nodesByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        allNodesData.forEach((node) => {
            const agentName = node.agent_name ?? "unknown"
            if (nodesByAgent[agentName] === undefined) {
                nodesByAgent[agentName] = []
            }
            nodesByAgent[agentName].push(node)
        })

        return nodesByAgent
    }

    const getValidNodesByAgent = () => {
        const nodesByAgent = getNodesByAgent()
        const validNodesByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        Object.entries(nodesByAgent).forEach(([agentName, nodes]) => {
            const validNodes = nodes.filter((node) => {
                const displayName = getNodeDisplayName(node)
                return displayName !== "" && displayName != null
            })
            if (validNodes.length > 0) {
                validNodesByAgent[agentName] = validNodes
            }
        })

        return validNodesByAgent
    }

    const getPromptsByAgent = () => {
        const nodesByAgent = getNodesByAgent()
        const promptsByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        Object.entries(nodesByAgent).forEach(([agentName, nodes]) => {
            const promptNodes = nodes.filter((node) => {
                if (node?.raw_prompt !== undefined && node?.raw_prompt !== "") {
                    return true
                }
                if (
                    node?.filled_prompt !== undefined &&
                    node?.filled_prompt !== ""
                ) {
                    return true
                }
                if (node?.prompt !== undefined && node?.prompt !== "") {
                    return true
                }
                return false
            })
            if (promptNodes.length > 0) {
                promptsByAgent[agentName] = promptNodes
            }
        })

        return promptsByAgent
    }

    return (
        <Box>
            {(() => {
                const agentInfo = getAgentInfo()
                const validNodesByAgent = getValidNodesByAgent()
                const promptsByAgent = getPromptsByAgent()

                if (agentInfo.length === 0) {
                    return null
                }

                return agentInfo.map((agentName, agentIndex) => {
                    const displayName = getAgentDisplayName(agentName)
                    const agentColor = getAgentColor(agentName)
                    const agentIcon = getAgentIcon(agentName)
                    const validNodes = validNodesByAgent[agentName] ?? []
                    const promptNodes = promptsByAgent[agentName] ?? []

                    return (
                        <Box key={agentIndex}>
                            {agentIndex === 0 ? (
                                <Box
                                    sx={{
                                        height: "1px",
                                        bgcolor: "grey.200",
                                        mb: 3,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        height: "1px",
                                        bgcolor: "grey.200",
                                        my: 3,
                                    }}
                                />
                            )}

                            <Box sx={{ mb: 4 }}>
                                <Box
                                    component="section"
                                    sx={{ marginBottom: 2 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ marginBottom: 0.5 }}
                                    >
                                        Agent
                                    </Typography>
                                    <Box
                                        sx={{
                                            bgcolor: agentColor,
                                            color: "white",
                                            padding: "5px",
                                            borderRadius: 1,
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {agentIcon}
                                        <Typography
                                            sx={{
                                                py: 0,
                                                px: 0.5,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {displayName}
                                        </Typography>
                                    </Box>
                                </Box>

                                {validNodes.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ marginBottom: 0.5 }}
                                        >
                                            {t`chat-message-journey.node-journey`}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ flexWrap: "wrap", gap: 1 }}
                                        >
                                            {validNodes.map((node, index) => {
                                                const nodeType = getNodeType(
                                                    node.node_name ?? "",
                                                )
                                                const nodeColor =
                                                    getNodeColor(nodeType)
                                                const displayName =
                                                    getNodeDisplayName(node)
                                                const tooltip = getNodeTooltip(
                                                    node.node_name ?? "",
                                                )

                                                return (
                                                    <Tooltip
                                                        key={index}
                                                        title={
                                                            <Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        mb: 1,
                                                                    }}
                                                                >
                                                                    {
                                                                        displayName
                                                                    }
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {tooltip}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <Chip
                                                            label={`${
                                                                index + 1
                                                            }. ${displayName}`}
                                                            color={nodeColor}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                mb: 1,
                                                                cursor: "help",
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Prompts for this agent */}
                                {promptNodes.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ marginBottom: 1 }}
                                        >
                                            {t`chat-message-journey.prompts.nodes-with-prompts`}
                                        </Typography>

                                        {promptNodes.map((node, index) => {
                                            const nodeType = getNodeType(
                                                node.node_name ?? "",
                                            )
                                            const nodeColor =
                                                getNodeColor(nodeType)
                                            const displayName =
                                                getNodeDisplayName(node)

                                            return (
                                                <Accordion
                                                    key={index}
                                                    sx={{ mb: 2 }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            <ExpandMore />
                                                        }
                                                        sx={{
                                                            bgcolor: "grey.50",
                                                            border: "1px solid",
                                                            borderColor:
                                                                "grey.200",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 2,
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{ flex: 1 }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {
                                                                        displayName
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    {nodeType}{" "}
                                                                    Node
                                                                </Typography>
                                                            </Box>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                            >
                                                                {Boolean(
                                                                    node.prompt_version,
                                                                ) && (
                                                                    <Chip
                                                                        label={`v${node.prompt_version}`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={
                                                                            nodeColor
                                                                        }
                                                                    />
                                                                )}
                                                                <Tooltip
                                                                    title={getNodeTypeTooltip(
                                                                        nodeType,
                                                                    )}
                                                                    placement="top"
                                                                    arrow
                                                                >
                                                                    <Chip
                                                                        label={
                                                                            nodeType
                                                                        }
                                                                        size="small"
                                                                        color={
                                                                            nodeColor
                                                                        }
                                                                        sx={{
                                                                            cursor: "help",
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Stack>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails
                                                        sx={{ pt: 2 }}
                                                    >
                                                        <Stack spacing={2}>
                                                            {Boolean(
                                                                node.filled_prompt,
                                                            ) && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        gutterBottom
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Code />
                                                                        {t`chat-message-journey.prompts.filled_prompt`}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            p: 2,
                                                                            bgcolor:
                                                                                "grey.50",
                                                                            borderRadius: 1,
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "0.875rem",
                                                                            whiteSpace:
                                                                                "pre-wrap",
                                                                            overflowX:
                                                                                "auto",
                                                                            border: "1px solid",
                                                                            borderColor:
                                                                                "grey.200",
                                                                            maxHeight:
                                                                                "300px",
                                                                            overflowY:
                                                                                "auto",
                                                                        }}
                                                                    >
                                                                        {formatPrompt(
                                                                            node.filled_prompt ??
                                                                                "",
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                            {/* Show raw_prompt when available */}
                                                            {Boolean(
                                                                node.raw_prompt,
                                                            ) && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        gutterBottom
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Code />
                                                                        {t`chat-message-journey.prompts.raw_prompt`}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            p: 2,
                                                                            bgcolor:
                                                                                "grey.100",
                                                                            borderRadius: 1,
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "0.875rem",
                                                                            whiteSpace:
                                                                                "pre-wrap",
                                                                            overflowX:
                                                                                "auto",
                                                                            border: "1px solid",
                                                                            borderColor:
                                                                                "grey.300",
                                                                            maxHeight:
                                                                                "200px",
                                                                            overflowY:
                                                                                "auto",
                                                                        }}
                                                                    >
                                                                        {formatPrompt(
                                                                            node.raw_prompt ??
                                                                                "",
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                            {/* Show legacy prompt when no filled/raw prompts are available */}
                                                            {!Boolean(
                                                                node.filled_prompt,
                                                            ) &&
                                                                !Boolean(
                                                                    node.raw_prompt,
                                                                ) &&
                                                                Boolean(
                                                                    node.prompt,
                                                                ) && (
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            <Code />
                                                                            {t`chat-message-journey.prompts.raw_prompt`}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                p: 2,
                                                                                bgcolor:
                                                                                    "grey.100",
                                                                                borderRadius: 1,
                                                                                fontFamily:
                                                                                    "monospace",
                                                                                fontSize:
                                                                                    "0.875rem",
                                                                                whiteSpace:
                                                                                    "pre-wrap",
                                                                                overflowX:
                                                                                    "auto",
                                                                                border: "1px solid",
                                                                                borderColor:
                                                                                    "grey.300",
                                                                                maxHeight:
                                                                                    "300px",
                                                                                overflowY:
                                                                                    "auto",
                                                                            }}
                                                                        >
                                                                            {formatPrompt(
                                                                                node.prompt ??
                                                                                    "",
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )}

                                                            {Boolean(
                                                                node.response,
                                                            ) && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        gutterBottom
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Message />
                                                                        {t`chat-message-journey.prompts.response`}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            p: 2,
                                                                            bgcolor:
                                                                                "grey.50",
                                                                            borderRadius: 1,
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "0.875rem",
                                                                            whiteSpace:
                                                                                "pre-wrap",
                                                                            overflowX:
                                                                                "auto",
                                                                            border: "1px solid",
                                                                            borderColor:
                                                                                "grey.200",
                                                                            maxHeight:
                                                                                "200px",
                                                                            overflowY:
                                                                                "auto",
                                                                        }}
                                                                    >
                                                                        {formatResponse(
                                                                            node.response ??
                                                                                "",
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    </AccordionDetails>
                                                </Accordion>
                                            )
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )
                })
            })()}
        </Box>
    )
}

export default PromptsSection
